// src/components/ISP/iarFeeTieredUtils.js

// IAR tiered fees are stored as an ordered array aligned to the active
// breakpoint schedule's labels: [{ label, rate }]. This shape (unlike a
// label-keyed map) supports schedules that legitimately repeat a label —
// e.g. two "Next $2,000,000" rows — without collapsing them. Readers run
// their stored value through normalizeIarFeeTiered against the *current*
// schedule labels so they tolerate the new array, the legacy dict left in
// old drafts/submissions, and any label/schedule change.

// Convert any stored value to [{ label, rate }] aligned to `labels`.
// - Array input: rate taken from the SAME position (positional alignment),
//   so duplicate labels keep their individual rates and a schedule swap
//   re-labels in place.
// - Legacy dict input: rate looked up BY label; duplicate labels past the
//   first can't be recovered from a dict, so they get "".
export function normalizeIarFeeTiered(value, labels) {
  const safeLabels = Array.isArray(labels) ? labels : [];
  if (Array.isArray(value)) {
    return safeLabels.map((label, i) => ({
      label,
      rate: value[i] && value[i].rate != null ? value[i].rate : "",
    }));
  }
  if (value && typeof value === "object") {
    // Track which labels have been consumed so that duplicate labels
    // beyond the first get "" (a dict cannot store two separate rates
    // under the same key, so only the first occurrence can be recovered).
    const consumed = Object.create(null);
    return safeLabels.map((label) => {
      if (!consumed[label] && value[label] != null) {
        consumed[label] = true;
        return { label, rate: value[label] };
      }
      return { label, rate: "" };
    });
  }
  return safeLabels.map((label) => ({ label, rate: "" }));
}

// Return a new normalized array with the rate at `index` replaced.
export function setTieredRate(value, labels, index, rate) {
  const next = normalizeIarFeeTiered(value, labels);
  if (index >= 0 && index < next.length) {
    next[index] = { ...next[index], rate };
  }
  return next;
}

// Normalize an entered IAR rate for display/output without converting it to a
// Number. July 18 feedback requires no more than two decimal places and exactly
// two decimals in output, so invalid legacy precision is deliberately returned
// unchanged for validation to reject rather than silently rounding it.
export function formatIarFeeRate(value) {
  if (value === null || value === undefined || value === "") return "";

  const raw = String(value).trim().replace(/%$/, "").trim();
  const match = raw.match(/^(\d*)(?:\.(\d{0,2}))?$/);
  if (!match || (!match[1] && !match[2])) return raw;

  const whole = match[1] || "0";
  const fraction = (match[2] || "").padEnd(2, "0");
  return `${whole}.${fraction}`;
}

export function formatIarFeePercent(value, emptyValue = "\u2014") {
  const formatted = formatIarFeeRate(value);
  return formatted === "" ? emptyValue : `${formatted}%`;
}

// Display-only: flatten either shape to [{ label, rate }] without needing
// the current labels (used by the read-only confirm summary).
export function toTieredEntries(value) {
  if (Array.isArray(value)) {
    return value
      .filter((t) => t != null && typeof t === "object")
      .map((t) => ({ label: String(t.label ?? ""), rate: t.rate ?? "" }));
  }
  if (value && typeof value === "object") {
    return Object.entries(value).map(([label, rate]) => ({ label, rate: rate ?? "" }));
  }
  return [];
}
